// @ts-check
'use strict';

/**
 * Controlled vocabularies for the condition report, ported from Site2Scope's
 * report redesign (Site2Scope_Condition_Report_Redesign_DesignSpec.md §7,
 * 8 Jul 2026), keyed by band instead of Site2Scope's red/amber/green.
 */

/** @typedef {import('./scales').Band} Band */
/** @typedef {'high' | 'medium' | 'low'} Priority */

/** Maintenance strategy per area — the report's action and outlook column. */
const DELIVERY_MODES = Object.freeze([
  { key: 'repaint_year', label: 'Repaint Year', description: 'Full recoat scheduled' },
  { key: 'freshen_coat', label: 'Freshen Coat', description: 'Single-coat cosmetic refresh' },
  { key: 'maintenance_year', label: 'Maintenance Year', description: 'Targeted remedial work' },
  { key: 'wash_down_year', label: 'Wash-Down Year', description: 'Clean only, protect coating life' },
  { key: 'targeted_maintenance', label: 'Targeted Maintenance', description: 'Spot repairs, specific elements' },
  { key: 'monitor_only', label: 'Monitor Only', description: 'Reassess next cycle' },
]);

/** @type {Readonly<Record<string, {key: string, label: string, description: string}>>} */
const DELIVERY_MODE_BY_KEY = Object.freeze(Object.fromEntries(DELIVERY_MODES.map((d) => [d.key, d])));

/** Suggested delivery mode for an area's band; the assessor confirms or changes it. */
const BAND_DEFAULT_DELIVERY = Object.freeze({ poor: 'repaint_year', fair: 'maintenance_year', good: 'monitor_only' });

/** Suggested priority for an area's band; sorts the dashboard. */
/** @type {Readonly<Record<Band, Priority>>} */
const BAND_DEFAULT_PRIORITY = Object.freeze({ poor: 'high', fair: 'medium', good: 'low' });

const PRIORITY_RANK = Object.freeze({ high: 3, medium: 2, low: 1 });

/** The report's Higgins Recommendation tiers. */
const BAND_RECOMMENDATION = Object.freeze({
  poor: { verb: 'Prioritise', tier: 'Poor condition', lead: 'Address as a first priority to protect the asset from further deterioration.' },
  fair: { verb: 'Consider', tier: 'Fair condition', lead: 'Plan and budget for intervention in the near future.' },
  good: { verb: 'Defer', tier: 'Good condition', lead: 'Well maintained — monitor at the next cycle and save on spend.' },
});

/** Site classification; adds a short lead-in paragraph to the report. */
const SECTORS = Object.freeze([
  { key: 'aged_care', label: 'Aged Care' },
  { key: 'childcare', label: 'Childcare' },
  { key: 'commercial', label: 'Commercial' },
  { key: 'health', label: 'Health' },
  { key: 'retirement_living', label: 'Retirement Living' },
]);

/** @type {Readonly<Record<string, {key: string, label: string}>>} */
const SECTOR_BY_KEY = Object.freeze(Object.fromEntries(SECTORS.map((s) => [s.key, s])));

/**
 * Site condition is proportional, so one bad item doesn't condemn a whole site:
 * poor if at least `poorShare` of rated items are poor; else fair if at least
 * `poorFairShare` are poor or fair; else good. Tunable after real data.
 */
const OVERALL_THRESHOLDS = Object.freeze({ poorShare: 0.10, poorFairShare: 0.25 });

module.exports = {
  DELIVERY_MODES,
  DELIVERY_MODE_BY_KEY,
  BAND_DEFAULT_DELIVERY,
  BAND_DEFAULT_PRIORITY,
  PRIORITY_RANK,
  BAND_RECOMMENDATION,
  SECTORS,
  SECTOR_BY_KEY,
  OVERALL_THRESHOLDS,
};
